export default {
  label: "Reddit Karma",
  script: `import { createSession } from '@plutoxyz/automation';
import { chromium } from 'playwright-core';

const PROVIDERS = [
  { id: 'credentials', label: 'Email / Username' },
  { id: 'google', label: 'Google' },
];
const LOGIN_URL = 'https://www.reddit.com/login/';
const HOME_URL = 'https://www.reddit.com';

/**
 * Detect if we run into a 2FA page and handle it
 * return Promise<void>
 */
const handleSecurityDetection = async () => {
  const security = !!(
    await page
      .getByRole('textbox', { name: 'Verification code' })
      .elementHandles()
  ).length;

  if (security) {
    console.log('Reddit 2FA detected, prompting for security code');

    const otp = await session.prompt({
      title: 'Enter one time code',
      description: 'Enter the one time code sent to your phone',
      prompts: [
        {
          label: 'Code',
          type: 'text',
          attributes: {},
        },
      ],
    });

    console.log('Filling security code');
    await page.getByRole('textbox', { name: 'Verification code' }).fill(otp[0]);
    console.log('Security code filled');
    await page.getByRole('button', { name: 'Check code' }).click();
    await page.waitForLoadState('domcontentloaded');
  } else {
    console.log('No security code detected, continuing');
  }
};

const promptAuthType = async () =>
  await session.prompt({
    title: 'Log in to Reddit',
    description: 'Enter your credentials to Log in',
    icon: 'reddit.com',
    prompts: [
      {
        label: 'Login method',
        type: 'login',
        attributes: { providers: PROVIDERS },
      },
    ],
  });

/** Initialize session and initial prompt */
const session = await createSession();

/** Browser Setup */
const cdp = await session.cdp();
const browser = await chromium.connectOverCDP(cdp);
const context = browser.contexts()[0];
const page = context.pages()[0];

const BLOCK_PATTERNS = ['**/home-feed/**', '**/analytics.js'];

for (const pattern of BLOCK_PATTERNS) {
  await context.route(pattern, (route) => {
    console.log('❌ blocked', route.request().url());
    route.abort();
  });
}

/**
 * Generic “ask again” prompt.  We fall back to this when the
 * login-type=login prompt returns an “Invalid username or password”
 * error that Reddit does not differentiate.
 */
const promptForCredentials = async (usernameError, passwordError) =>
  session.prompt({
    title: 'Login to Reddit',
    description: 'Enter your Reddit credentials to prove your data',
    prompts: [
      {
        label: 'Email or username',
        type: 'text',
        attributes: { min_length: 3, placeholder: 'Email or username' },
        error: usernameError,
      },
      {
        label: 'Password',
        type: 'password',
        attributes: { placeholder: 'Password' },
        error: passwordError,
      },
    ],
  });

/**
 * Fills the username and password fields on the Reddit login page.
 * This function no longer prompts the user for credentials.
 */
const authUsernamePass = async (username, password) => {
  console.log('Filling username');
  await Promise.all([
    page.getByRole('textbox', { name: /Email or username/i }).waitFor(),
    page.getByRole('textbox', { name: /Password/i }).waitFor(),
  ]);

  await page
    .getByRole('textbox', { name: /Email or username/i })
    .fill(username);
  await page.getByRole('textbox', { name: /Password/i }).fill(password);
  await page.getByRole('button', { name: /Log In/i }).click();

  const errorText = page.getByText(/invalid username|something went wrong/i);
  const mfa = page.getByRole('textbox', { name: 'Verification code' });
  const createButton = page.getByRole('link', { name: 'Create post' });

  await Promise.race([
    mfa.waitFor({ state: 'visible', timeout: 20000 }),
    createButton.waitFor({ state: 'visible', timeout: 20000 }),
    errorText.waitFor({ state: 'visible', timeout: 20000 }),
  ]);

  try {
    const isCreateButtonVisible = await createButton.isVisible({
      timeout: 2500,
    });
    if (isCreateButtonVisible) {
      return;
    }
  } catch (e) {}

  /* ── Invalid credentials handling ───────────────────────────── */
  // Multiple types of error UIs surfacing: Either traditional helper-text or the newer banner variant
  const sawError = await errorText
    .isVisible({ timeout: 2500 })
    .catch(() => false);

  console.log('sawError', sawError);

  if (sawError) {
    console.log('Invalid username or password - re-prompting user');

    const [newUsername, newPassword] = await promptForCredentials(
      'Invalid username or password.',
      'Invalid username or password.'
    );

    return authUsernamePass(newUsername, newPassword);
  }

  const has2FA = await page
    .getByRole('textbox', { name: 'Verification code' })
    .isVisible();

  if (has2FA) {
    console.log('Detected 2-Step Verification');
    const [code] = await session.prompt({
      title: 'Enter one time code',
      description: 'Enter the one time code from your authenticator app',
      prompts: [
        {
          label: 'Code',
          type: 'text',
          attributes: {},
        },
      ],
    });

    await page.getByRole('textbox', { name: 'Verification code' }).fill(code);
    await page.getByRole('button', { name: 'Check code' }).click();
  } else {
    console.log('No 2-Step Verification detected, continuing');
  }
};

const authGoogle = async (usernameError, passwordError) => {
  // The Google button lives inside a GIS iframe.  Grab that frame first.
  const gisFrame = page.frameLocator(
    'iframe[src*="accounts.google.com/gsi/"][src*="button"]'
  );

  // Locator *inside* the frame; keeps re-evaluating if GIS re-renders it.
  const googleBtn = gisFrame
    .locator('div[role="button"]')
    .filter({ hasText: /continue with google/i });

  console.log('Clicking "Continue with Google" (inside GIS iframe)…');

  const [googlePage] = await Promise.all([
    page.waitForEvent('popup'), // capture the FedCM popup
    googleBtn.click(), // trigger the click
  ]);

  console.log('Google popup captured');

  console.log('Prompting user for credentials');
  const [username, password] = await session.prompt({
    title: 'Google Email or Phone',
    description: 'Enter your Google credentials to login',
    icon: 'google.com',
    prompts: [
      {
        label: 'Email or phone',
        type: 'text',
        attributes: {
          min_length: 3,
          max_length: 3,
          placeholder: 'Email or phone',
        },
        error: usernameError,
      },
      {
        label: 'Password',
        type: 'password',
        attributes: { placeholder: 'Password' },
        error: passwordError,
      },
    ],
  });

  console.log('Filling Email');
  await googlePage.waitForLoadState('domcontentloaded');
  await googlePage
    .getByRole('textbox', { name: 'Email or phone' })
    .fill(username, { timeout: 5000 });
  console.log('Email filled');
  await googlePage.getByText('Next').click();
  // After the email step Google may either:
  //  • navigate straight to the password page, OR
  //  • show an extra screen with a “Try another way” button.
  //
  // We race those two possibilities.

  // Helper: waits *up to* 4 s for the optional “Try another way” screen.
  const handleOptionalTryAnother = async (_page) => {
    const tryAnotherBtn = _page.getByRole('button', {
      name: /try another way/i,
    });

    const appeared = await tryAnotherBtn
      .waitFor({ state: 'visible', timeout: 4000 })
      .then(() => true)
      .catch(() => false); // → timed-out, button never showed

    if (!appeared) {
      console.log('No optional screen — continuing normally');
      return;
    }

    console.log('Optional “Try another way” screen detected → clicking');
    await Promise.all([
      _page.waitForNavigation({ waitUntil: 'domcontentloaded' }),
      tryAnotherBtn.click(),
    ]);
    console.log('"Try another way" handled');
  };

  // Race: either we arrive at the password URL, or we detect & handle the
  // optional screen.  Whichever finishes first unblocks the flow.
  await Promise.race([
    googlePage.waitForURL(/\\/signin\\/v2\\/sl\\/pwd/, { timeout: 20000 }),
    handleOptionalTryAnother(googlePage),
  ]);

  // ── Optional “Choose how you’ll sign in” screen ──────────────────────────
  const handleOptionalEnterPassword = async (_page) => {
    const enterPwdBtn = _page.getByRole('link', {
      name: /enter your password/i,
    });

    const appeared = await enterPwdBtn
      .waitFor({ state: 'visible', timeout: 4000 })
      .then(() => true)
      .catch(() => false); // timed out → screen never appeared

    if (!appeared) {
      console.log('No “Enter your password” screen—continuing');
      return;
    }

    console.log('Optional screen detected → clicking “Enter your password”');
    await Promise.all([
      _page.waitForNavigation({ waitUntil: 'domcontentloaded' }),
      enterPwdBtn.click(),
    ]);
    console.log('“Enter your password” clicked, navigated to password page');
  };

  // Give that screen up to 4 s to show.  If we’re *already* on the
  // password page nothing happens because the button isn’t present.

  await Promise.race([
    googlePage.waitForURL(/\\/signin\\/v2\\/sl\\/pwd/, { timeout: 20000 }),
    await handleOptionalEnterPassword(googlePage),
  ]);

  const pwdBox = googlePage.getByRole('textbox', {
    name: 'Enter your password',
  });
  await pwdBox.waitFor({ state: 'visible' });
  await pwdBox.fill(password, { timeout: 5000 });
  console.log('Password filled');
  await googlePage.getByText('Next').click();

  const navOrClose = Promise.race([
    googlePage.waitForEvent('close').then(() => 'closed'),
    googlePage
      .waitForNavigation({ waitUntil: 'networkidle' })
      .then(() => 'navigated'),
  ]);

  const result = await navOrClose;
  if (result === 'closed') {
    console.log('Google popup closed – login finished without 2-FA');
    return;
  }

  await googlePage.waitForTimeout(2000);

  const tryAnother = googlePage.getByRole('button', {
    name: 'Try another way',
  });
  if ((await tryAnother.elementHandles()).length) {
    console.log("Clicking 'Try another way'");
    await tryAnother.click();
    await googlePage.waitForTimeout(3000);
  } else {
    console.log("'Try another way' not present - continuing");
  }

  const has2FA = !!(
    await googlePage.getByText('2-Step Verification').elementHandles()
  ).length;

  if (has2FA) {
    console.log('Detected 2-Step Verification');

    const hasOptionalScreen = googlePage.getByText(
      'fingerprint, face, or screen lock'
    );
    if (!!(await hasOptionalScreen.elementHandles()).length) {
      console.log('Detected extra 2fa screen, continuing');
      await googlePage.getByRole('button', { name: 'Try another way' }).click();
      await googlePage.waitForTimeout(1000);
    }

    const tabletOption = {
      label: 'Tap Yes on your phone or tablet',
      locator: googlePage.getByRole('link', {
        name: ' on your phone or tablet',
      }),
      followup: async (_page, _code) => {},
      prompt: async () => ({
        title: 'Confirm Access',
        description: 'Confirm you allowed access in your Google App',
        prompts: [
          {
            label: 'Confirm to continue',
            type: 'checkbox',
            attributes: { required: true, options: ['Confirm'] },
          },
        ],
      }),
    };
    const authenticator = {
      label: 'Get a verification code from the Google Authenticator app',
      locator: googlePage.getByRole('link', {
        name: 'Get a verification code from',
      }),
      followup: async (_page, _code) => {
        await _page.getByRole('textbox', { name: 'Enter code' }).fill(_code);
        await _page.getByText('Next').click();
      },
      prompt: () => ({
        title: 'Enter one time code',
        description: 'Enter the one time code sent to your phone',
        prompts: [
          {
            label: 'Code',
            type: 'text',
            attributes: {},
          },
        ],
      }),
    };
    const smsCode = {
      label: 'Get a verification code sent to your phone',
      locator: googlePage.getByRole('link', {
        name: 'Get a verification code at',
      }),
      followup: async (_page, _code) => {
        await _page
          .getByRole('textbox', { name: 'Enter the code' })
          .fill(_code);
        await _page.getByText('Next').click();
      },
      prompt: () => ({
        title: 'Enter one time code',
        description: 'Enter the one time code sent to your phone',
        prompts: [
          {
            label: 'Code',
            type: 'text',
            attributes: {},
          },
        ],
      }),
    };

    const allOptions = [tabletOption, authenticator, smsCode];
    const checkPromises = allOptions.map(async (option) => {
      try {
        if (!(await option.locator.elementHandles()).length) {
          console.warn(\`Option '\${option.label}' locator not visible.\`);
          return null;
        }

        const isEnabled = !(await option.locator.isDisabled());
        if (!isEnabled) {
          console.warn(
            \`Found disabled option: \${await option.locator.innerText()}\`
          );
        } else {
          console.log(
            \`Found enabled option \${await option.locator.innerText()}\`
          );
        }

        return isEnabled ? option : null;
      } catch (error) {
        console.error(\`Error checking option '\${option.label}':\`, error);
        return null;
      }
    });

    const checkedOptions = await Promise.all(checkPromises);
    const enabledOptions = checkedOptions.filter((o) => !!o);
    if (enabledOptions.length > 0) {
      if (enabledOptions.length > 1) {
        console.log(
          \`Prompting user with enabled 2-Factor options.\${(
            await Promise.all(
              enabledOptions.map(
                async (e, i) => \`\${i + 1}) \${await e.locator.innerText()}\`
              )
            )
          ).join('\\n')}\`
        );

        const [choice] = await session.prompt({
          title: 'Two Factor Authentication',
          description: "Choose how you'd like to 2FA",
          prompts: [
            {
              label: 'Two Factor Method',
              type: 'checkbox',
              attributes: {
                multiple: false,
                options: enabledOptions.map((o) => o.label),
              },
            },
          ],
        });

        console.log('Selecting 2FA method');
        const match = enabledOptions.findIndex((i) => i.label === choice[0]);
        await enabledOptions[match].locator.click();
        console.log('Prompting user for 2FA');
        const [code] = await session.prompt(
          await enabledOptions[match].prompt()
        );

        if (enabledOptions[match].followup) {
          console.log('Entering 2FA Code');
          await enabledOptions[match].followup(googlePage, code);
          console.log('2FA code entered');
        }
      } else {
        console.log(
          \`Found a single enabled option: \${await enabledOptions[0].locator.innerText()}\`
        );

        await enabledOptions[0].locator.click();
        console.log('Prompting user for 2FA');
        const [code] = await session.prompt(await enabledOptions[0].prompt());
        if (enabledOptions[0].followup) {
          console.log('Entering 2FA Code');
          await enabledOptions[0].followup(googlePage, code);
          console.log('2FA code entered');
        }
      }
    } else {
      console.log('No enabled options found.');
    }
  }
};

await page.goto(HOME_URL, { waitUntil: 'domcontentloaded' });
const isUserLoggedIn =
  (await page.locator('#login-button').elementHandles()).length === 0;

console.log("User is " + (isUserLoggedIn ? "logged in" : "not logged in"));
if (!isUserLoggedIn) {
  console.log(\`Navigating to \${LOGIN_URL}\`);
  console.log('Prompting for auth type');

  const urlPromise =  page.goto(LOGIN_URL, { waitUntil: 'domcontentloaded' });
  const credentialsPromise = promptAuthType();

  const [, [choice]] = await Promise.all([
    urlPromise,
    credentialsPromise,
  ]);

  console.log('Login choice →', choice);
  await handleSecurityDetection();

  switch (choice.id) {
    case 'google':
      console.log('Authing Google');
      await authGoogle(undefined, undefined);
      await page.waitForLoadState('domcontentloaded');
      await handleSecurityDetection();
      break;
    case 'credentials':
      console.log('Email →', choice.email);
      console.log('Password →', choice.password);
      await authUsernamePass(choice.email, choice.password);
      break;
  }

  console.log('User Logged In');
  await page.waitForLoadState('domcontentloaded');
}

/**
 * Navigate to User Profile
 */

console.log('Navigating to User Profile');
const userMenuBtn = page.getByRole('button', { name: /expand user menu$/i });
await userMenuBtn.waitFor({ state: 'visible', timeout: 30000 });
await userMenuBtn.click();
await page.getByText('View Profile').click();

/**
 * Scrape Username
 */
await page.waitForLoadState('domcontentloaded');

console.log('User Profile Loaded');
console.log('Scraping Username');
const handle = await page
  .getByLabel('Profile information')
  .getByRole('heading', {
    name: /^(?!\\s*(?:Settings|Social\\s+Links|Trophy\\s+Case)\\s*$).+/i,
  })
  .innerText();
console.log('Username Scraped');

const [postKarma, commentKarma] = await page
  .getByTestId('karma-number')
  .allInnerTexts();

/**
 * Generate proof and cleanup
 */
console.log(\`Generating proof of username \${handle}\`);
await session.prove('reddit-data', [
  { redditUsername: handle },
  {
    totalKarma: (
      Number.parseInt(postKarma.replace(',', ''), 10) +
      Number.parseInt(commentKarma.replace(',', ''), 10)
    ).toLocaleString('en-US'),
  },
  { postKarma },
  { commentKarma },
]);
console.log('Proof generated');

console.log('Performing cleanup');
await page.close();
await browser.close();
await session.close();`,
};
